import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import useSeo from '@/hooks/useSeo';

export default function CaseMajorRepairRedutorPanelaGusaPage() {
  useSeo({
    title: 'Major Repair de Redutor Industrial | Oficina Especializada | Grupo Pilar',
    description: 'Case de recuperação completa de redutor industrial utilizado no sistema de içamento da tampa de panela de gusa, envolvendo mecânica, pneumática, caldeiraria e soldagem industrial.',
    keywords: 'major repair, redutor industrial, manutenção de redutor, recuperação de redutor, siderurgia, panela de gusa, motor pneumático industrial, caldeiraria industrial, oficina especializada, grupo pilar',
    canonical: 'https://www.pilarmanutencao.com.br/cases/major-repair-redutor-tampa-panela-gusa',
    ogUrl: 'https://www.pilarmanutencao.com.br/cases/major-repair-redutor-tampa-panela-gusa',
    ogImage: 'https://readdy.ai/api/search-image?query=Extremely%20realistic%20professional%20industrial%20photography%20modern%20clean%20organized%20heavy%20industrial%20workshop%20with%20large%20machinery%20hydraulic%20equipment%20steel%20components%20metallic%20surfaces%20professional%20technicians%20in%20safety%20gear%20working%20not%20facing%20camera%20cinematic%20dramatic%20lighting%20gray%20silver%20steel%20tones%20with%20subtle%20warm%20yellow%20accent%20details%20wide%20angle%20widescreen%20composition%20premium%20high%20resolution%20sharp%20depth%20industrial%20maintenance%20engineering%20reliability%20heavy%20mechanics%20pulp%20paper%20mining%20oil%20gas%20sectors%20negative%20space%20for%20title%20overlay%20clean%20backgrounds%20not%20stock%20photo%20not%20illustration%20not%203D%20render&width=1600&height=700&seq=gp-cases-unified-hero-v2&orientation=landscape',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const heroImage = 'https://readdy.ai/api/search-image?query=Extremely%20realistic%20professional%20industrial%20photography%20modern%20clean%20organized%20heavy%20industrial%20workshop%20with%20large%20machinery%20hydraulic%20equipment%20steel%20components%20metallic%20surfaces%20professional%20technicians%20in%20safety%20gear%20working%20not%20facing%20camera%20cinematic%20dramatic%20lighting%20gray%20silver%20steel%20tones%20with%20subtle%20warm%20yellow%20accent%20details%20wide%20angle%20widescreen%20composition%20premium%20high%20resolution%20sharp%20depth%20industrial%20maintenance%20engineering%20reliability%20heavy%20mechanics%20pulp%20paper%20mining%20oil%20gas%20sectors%20negative%20space%20for%20title%20overlay%20clean%20backgrounds%20not%20stock%20photo%20not%20illustration%20not%203D%20render&width=1600&height=700&seq=gp-cases-unified-hero-v2&orientation=landscape';

  return (
    <div className="min-h-screen bg-gp-graphite">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroImage}
              alt="Motoredutor industrial recuperado, pintado e pronto para retorno à operação — Major Repair executado pela Pilar na oficina especializada"
              title="Motoredutor industrial recuperado para panela de gusa — Case Pilar"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gp-graphite/75"></div>
          </div>
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-28 pb-16">
            <div className="max-w-4xl">
              <div className="flex items-center gap-3 mb-6 flex-wrap">
                <span className="inline-block px-3 py-1 rounded-sm text-xs font-semibold uppercase tracking-wider bg-gp-yellow text-gp-graphite">
                  Oficina Especializada
                </span>
                <span className="text-gray-400 text-xs uppercase tracking-wider">Siderurgia</span>
                <span className="text-gray-500 text-xs">— Aracruz, ES</span>
              </div>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase leading-tight mb-6">
                <span className="text-gp-yellow">Case:</span>{' '}
                Major Repair de Redutor de Içamento da Tampa de Panela de Gusa
              </h1>
              <p className="text-gray-300 text-lg max-w-2xl leading-relaxed">
                Major Repair completo de redutor industrial responsável pelo sistema de içamento da tampa de panela de gusa em operação siderúrgica,
                incluindo revisão do motor pneumático de grande porte, recuperação estrutural, caldeiraria e soldagem industrial.
              </p>
            </div>
          </div>
        </section>

        {/* Visão Geral */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Visão Geral</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-information-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Dados do Projeto</h3>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-settings-3-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Equipamento</span>
                        <p className="text-gray-300 text-sm">Redutor de Içamento da Tampa de Panela de Gusa</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-building-2-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Segmento</span>
                        <p className="text-gray-300 text-sm">Siderurgia</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-tools-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Aplicação</span>
                        <p className="text-gray-300 text-sm">Sistema de içamento da tampa de panela de gusa</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-alert-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Criticidade</span>
                        <p className="text-gray-300 text-sm">Equipamento crítico — indisponibilidade impacta diretamente a operação</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-tools-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Disciplinas</span>
                        <p className="text-gray-300 text-sm">Mecânica, Pneumática, Caldeiraria, Soldagem e Hidráulica</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-award-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Diferencial</span>
                        <p className="text-gray-300 text-sm">Serviço homologado após desenvolvimento técnico — anteriormente executado por outro fornecedor</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <img
                    src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/1b37e3ef-b215-4ce2-9fb5-e4f7c5474484_compressed_MONTAGEM-ENGRENAGENS.webp"
                    alt="Conjunto principal do acionamento da Tampa da Panela de Gusa durante processo de remontagem e recuperação na oficina Pilar"
                    title="Conjunto do acionamento da panela de gusa em remontagem na oficina Pilar"
                    className="w-full h-auto object-contain p-4 bg-gp-graphite"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Conjunto principal do acionamento da Tampa da Panela de Gusa durante processo de remontagem e recuperação.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Desafio */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Desafio</h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-alert-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-gray-300 text-base leading-relaxed mb-4">
                        A Pilar executa o Major Repair de redutores responsáveis pelo sistema de içamento da tampa de panela de gusa em operação siderúrgica.
                        Trata-se de um equipamento crítico para o processo produtivo, cuja indisponibilidade impacta diretamente a operação.
                      </p>
                      <p className="text-gray-300 text-base leading-relaxed mb-4">
                        O serviço era anteriormente realizado por outro fornecedor. Após processo de desenvolvimento técnico e homologação,
                        a Pilar passou a executar esse serviço, demonstrando capacidade técnica para atender aos rigorosos padrões do setor siderúrgico.
                      </p>
                      <p className="text-gray-400 text-sm leading-relaxed mb-4">
                        As principais complexidades envolviam:
                      </p>
                      <ul className="space-y-2 mb-4">
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Equipamento de alta criticidade operacional para o processo siderúrgico</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Necessidade de desmontagem completa para acesso a todos os componentes</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Grande quantidade de componentes mecânicos e pneumáticos para recuperação</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Recuperação estrutural com recondicionamento de componentes de desgaste</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Elevado grau de precisão durante montagem e ajustes do conjunto</span>
                        </li>
                        <li className="flex items-start gap-2 text-gray-300 text-sm">
                          <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                          <span>Revisão completa do motor pneumático de grande porte</span>
                        </li>
                      </ul>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        O principal desafio era executar o Major Repair completo com a precisão e qualidade exigidas pelo setor siderúrgico,
                        consolidando a Pilar como fornecedor homologado para este serviço crítico.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <img
                    src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/662ffcc1-f991-4ee5-8aac-b874711e2875_compressed_FREIO-MOTORREDUTOR.webp"
                    alt="Sistema de freio do conjunto de acionamento da Tampa da Panela de Gusa durante processo de recuperação mecânica na oficina Pilar"
                    title="Sistema de freio do motoredutor em recuperação na oficina Pilar"
                    className="w-full h-auto object-contain p-4 bg-gp-graphite"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Sistema de freio do conjunto durante processo de recuperação mecânica.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Análise Técnica (Motor Pneumático) */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Análise Técnica</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start mb-8">
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <img
                    src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/fd990d41-e762-4aaa-84c0-c8bb103b90b0_compressed_MONTAGEM-ROLAMENTOS.webp"
                    alt="Análise e substituição de rolamentos do conjunto de acionamento da Tampa da Panela de Gusa — restabelecimento da confiabilidade operacional na oficina Pilar"
                    title="Análise e substituição de rolamentos na oficina Pilar"
                    className="w-full h-auto object-contain p-4 bg-gp-graphite"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Análise e substituição de rolamentos visando restabelecimento da confiabilidade operacional.
                    </p>
                  </div>
                </div>

                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-settings-3-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Atividades Executadas</h3>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Desmontagem integral do motor pneumático de grande porte</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Recuperação de componentes internos com recondicionamento dimensional</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Recuperação de cilindros com usinagem e ajustes de precisão</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Recuperação de êmbolos com recondicionamento de superfícies</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Fabricação de componentes de desgaste sob medida</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Substituição de anéis de vedação e componentes de desgaste</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Ajustes de funcionamento e testes funcionais completos</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Escopo Executado */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Escopo Executado pela Pilar</h2>
              </div>

              <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter mb-8">
                <img
                  src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/226203db-6c0d-4364-82bc-046413affc80_compressed_ENGRENAGEM-MONTADA.webp"
                  alt="Montagem e ajuste dos componentes de transmissão do conjunto de acionamento da Tampa da Panela de Gusa após recuperação mecânica na oficina Pilar"
                  title="Montagem e ajuste dos componentes de transmissão na oficina Pilar"
                  className="w-full h-auto object-contain p-4 bg-gp-graphite"
                  loading="lazy"
                />
                <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                  <p className="text-gray-400 text-xs text-center">
                    Montagem e ajuste dos componentes de transmissão após recuperação mecânica.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">01</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Desmontagem completa do redutor industrial na oficina especializada Pilar</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">02</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Inspeção dimensional detalhada de todos os componentes mecânicos e pneumáticos</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">03</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Recuperação de componentes mecânicos com recondicionamento de superfícies de desgaste</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">04</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Reparo de caldeiraria industrial para correções e reforços estruturais</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">05</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Reparo estrutural das guias de corrente com recuperação dimensional</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">06</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Revisão completa do sistema de freio com substituição de componentes de desgaste</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">07</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Substituição de componentes de desgaste com peças originais e fabricadas sob medida</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">08</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Inspeção detalhada e substituição de rolamentos conforme diagnóstico técnico</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">09</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Revisão completa do motor pneumático de grande porte (desmontagem, recuperação de cilindros e êmbolos, substituição de anéis de vedação)</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-5">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-gp-yellow font-display font-bold text-sm">10</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed pt-2">Montagem final de todos os componentes com ajustes de precisão e testes operacionais</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Caldeiraria e Soldagem */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Caldeiraria e Soldagem</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg p-6 md:p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-hammer-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Serviços Executados</h3>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Recuperação das guias de corrente com reconstituição dimensional</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Correções estruturais em componentes submetidos a esforços severos</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Recuperação de componentes sujeitos a desgaste severo na operação siderúrgica</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Aplicação de processos de soldagem industrial com controle de qualidade</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Reforços estruturais para aumento da resistência mecânica do conjunto</span>
                    </li>
                  </ul>
                </div>
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter">
                  <img
                    src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/d0c85d9e-c0bc-4f50-bfa1-354683c3b75d_compressed_GUIA-CORRENTE-REPARADA.webp"
                    alt="Guia de corrente reparada durante processo de recuperação estrutural do conjunto de acionamento da Tampa da Panela de Gusa — caldeiraria e soldagem industrial executadas pela Pilar"
                    title="Guia de corrente reparada — caldeiraria e soldagem Pilar"
                    className="w-full h-auto object-contain p-4 bg-gp-graphite"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Guia de corrente reparada durante processo de recuperação — caldeiraria e soldagem industrial executadas pela Pilar
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Sistema de Freio */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Revisão do Sistema de Freio</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="rounded-lg overflow-hidden border border-gp-graphite-lighter order-2 lg:order-1">
                  <img
                    src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/8cd9ed48-5714-4044-88fa-4c88796f94a8_compressed_TESTE-GUIA-CORRENTE.webp"
                    alt="Teste de funcionamento da guia de corrente do conjunto de acionamento após recuperação — validação do sistema de freio e acionamento na oficina Pilar"
                    title="Teste da guia de corrente após recuperação na oficina Pilar"
                    className="w-full h-auto object-contain p-4 bg-gp-graphite"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Teste de funcionamento da guia de corrente após recuperação — validação do sistema de acionamento
                    </p>
                  </div>
                </div>

                <div className="bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-6 md:p-8 order-1 lg:order-2">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-stop-circle-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-white uppercase mb-2">Atividades no Sistema de Freio</h3>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed mb-4">
                    O sistema de freio do redutor é um componente crítico de segurança, responsável por manter a posição exata
                    da tampa da panela de gusa durante o içamento. A revisão completa garantiu que o sistema retornasse à operação
                    com confiabilidade total.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Inspeção detalhada de todos os componentes do sistema de frenagem</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Substituição de pastilhas e lonas de freio desgastadas</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Recuperação de cilindros e atuadores do sistema de freio</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Ajustes de torque e regulagens de precisão do conjunto</span>
                    </li>
                    <li className="flex items-start gap-2 text-gray-300 text-sm">
                      <i className="ri-checkbox-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>Testes de frenagem e verificação de segurança operacional</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Resultados */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Resultados Obtidos</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <div className="rounded-lg overflow-hidden border border-gp-yellow/20">
                  <img
                    src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/87c52a18-1bdf-4553-9e86-09666ce9c758_compressed_MOTOREDUTOR-PRONTO-PINTURA.webp"
                    alt="Motoredutor industrial totalmente recuperado após Major Repair completo — equipamento pintado e pronto para retorno à operação siderúrgica"
                    title="Motoredutor recuperado após Major Repair pela Pilar"
                    className="w-full h-auto object-contain p-4 bg-gp-graphite"
                    loading="lazy"
                  />
                  <div className="bg-gp-graphite-light p-3 border-t border-gp-graphite-lighter">
                    <p className="text-gray-400 text-xs text-center">
                      Motoredutor recuperado, pintado e pronto para retorno à operação.
                    </p>
                  </div>
                </div>

                <div className="relative rounded-lg overflow-hidden border border-gp-yellow/20 bg-gp-graphite-light p-6 md:p-8">
                  <div className="absolute top-0 left-0 w-1 h-full bg-gp-yellow"></div>
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-trophy-line text-gp-yellow text-xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-bold text-white uppercase mb-2">Resultados</h3>
                    </div>
                  </div>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-refresh-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Retorno Operacional</span>
                        <p className="text-gray-400 text-sm">Equipamento totalmente recuperado e retornado às condições operacionais com confiabilidade</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-shield-check-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Confiabilidade</span>
                        <p className="text-gray-400 text-sm">Aumento significativo da confiabilidade do sistema de içamento da panela de gusa</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-alert-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Redução de Risco</span>
                        <p className="text-gray-400 text-sm">Redução do risco de falhas operacionais no processo siderúrgico</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-money-dollar-circle-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Alto Valor Agregado</span>
                        <p className="text-gray-400 text-sm">Recuperação de componentes de alto valor agregado com recondicionamento técnico</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-heart-pulse-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Vida Útil</span>
                        <p className="text-gray-400 text-sm">Ampliação da vida útil do redutor industrial com recuperação completa</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <i className="ri-award-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div>
                        <span className="text-gp-yellow font-semibold text-sm">Homologação</span>
                        <p className="text-gray-400 text-sm">Consolidação da Pilar como fornecedor homologado para Major Repairs no setor siderúrgico</p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Diferenciais da Pilar */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Diferenciais da Pilar</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-tools-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Capacidade de executar Major Repairs em redutores industriais de alta criticidade</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-settings-3-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Integração de mecânica, pneumática, caldeiraria, soldagem e hidráulica em uma única intervenção</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-team-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Equipe especializada com conhecimento técnico em redutores e sistemas de içamento industrial</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-home-gear-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Oficina estruturada e equipada para receber equipamentos de grande porte do setor siderúrgico</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-award-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Processo técnico de homologação para serviços anteriormente executados por outro fornecedor</p>
                </div>
                <div className="flex items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter rounded-lg p-5 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-settings-3-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">Domínio em revisão de motores pneumáticos de grande porte com recuperação de componentes internos</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Galeria Técnica */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider">Galeria Técnica</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Foto 1 */}
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter group">
                  <div className="h-56 flex items-center justify-center bg-gp-graphite p-2">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/4222bf0e-45a6-40e1-8f56-37fccde8297a_compressed_CAMISA-MOTOR-PNEUMATICO-PISTAO.webp"
                      alt="Camisa do motor pneumático de pistão durante processo de recuperação na oficina especializada Pilar"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gp-graphite/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-gray-300 text-xs text-center px-4">Camisa do motor pneumático de pistão em recuperação</span>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 1</span>
                  </div>
                </div>

                {/* Foto 2 */}
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter group">
                  <div className="h-56 flex items-center justify-center bg-gp-graphite p-2">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/4684a1b9-201f-4765-a8b5-3a5749bb9516_compressed_MONTAGEM-ENGRENAGEM-CORRENTE.webp"
                      alt="Montagem do conjunto engrenagem-corrente do sistema de acionamento da Tampa da Panela de Gusa na oficina Pilar"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gp-graphite/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-gray-300 text-xs text-center px-4">Montagem do conjunto engrenagem e corrente</span>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 2</span>
                  </div>
                </div>

                {/* Foto 3 */}
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter group">
                  <div className="h-56 flex items-center justify-center bg-gp-graphite p-2">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/e6f0581a-f56d-474d-81ee-78ecac1d870c_compressed_MONTAGEM-MOTOR-PNEUMATICO-PISTAO.webp"
                      alt="Montagem do motor pneumático de pistão — componente do sistema de acionamento da panela de gusa em recuperação na oficina Pilar"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gp-graphite/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-gray-300 text-xs text-center px-4">Montagem do motor pneumático de pistão</span>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 3</span>
                  </div>
                </div>

                {/* Foto 4 */}
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter group">
                  <div className="h-56 flex items-center justify-center bg-gp-graphite p-2">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/431ed73b-5581-4f18-b5f4-a55b7bcdda4e_compressed_MONTAGEM-MOTOR-PNEUMATICO-PISTAO2.webp"
                      alt="Segunda etapa da montagem do motor pneumático de pistão — ajustes e verificação de componentes na oficina Pilar"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gp-graphite/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-gray-300 text-xs text-center px-4">Montagem do motor pneumático — etapa de ajustes</span>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 4</span>
                  </div>
                </div>

                {/* Foto 5 */}
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter group">
                  <div className="h-56 flex items-center justify-center bg-gp-graphite p-2">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/620e6b68-c9c4-4a7e-8333-9031ccfa9814_compressed_MONTAGEM-MOTOR-PNEUMATICO-PISTAO3.webp"
                      alt="Etapa final da montagem do motor pneumático de pistão — verificação de alinhamento e folgas na oficina Pilar"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gp-graphite/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-gray-300 text-xs text-center px-4">Montagem final do motor pneumático de pistão</span>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 5</span>
                  </div>
                </div>

                {/* Foto 6 */}
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter group">
                  <div className="h-56 flex items-center justify-center bg-gp-graphite p-2">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/331eb3ac-3a33-4c13-9e86-c3ac15afc3b2_compressed_MONTAGEM-ROLAMENTOS-CONICOS-MOTOR-PNEUMATICO.webp"
                      alt="Montagem de rolamentos cônicos no motor pneumático — inspeção e substituição de componentes rotativos na oficina Pilar"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gp-graphite/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-gray-300 text-xs text-center px-4">Montagem de rolamentos cônicos no motor pneumático</span>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 6</span>
                  </div>
                </div>

                {/* Foto 7 */}
                <div className="relative rounded-lg overflow-hidden border border-gp-graphite-lighter group">
                  <div className="h-56 flex items-center justify-center bg-gp-graphite p-2">
                    <img
                      src="https://storage.readdy-site.link/project_files/93d54813-2fc1-4495-a318-6b5d36c5e53e/3bc93605-0a39-4553-a798-1a762efae5a7_compressed_PERIFERICO-REDUTOR.webp"
                      alt="Componente periférico do redutor industrial durante inspeção e recuperação na oficina especializada Pilar"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gp-graphite/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-gray-300 text-xs text-center px-4">Componente periférico do redutor em recuperação</span>
                  </div>
                  <div className="absolute bottom-2 left-2 bg-gp-graphite/80 px-2 py-1 rounded-sm">
                    <span className="text-gp-yellow text-xs font-semibold">Foto 7</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Serviços Relacionados */}
        <section className="relative py-16 overflow-hidden border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="font-display text-lg font-bold text-white uppercase tracking-wider">Serviços Relacionados</h2>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/mecanica-industrial"
                  className="inline-flex items-center gap-2 bg-gp-graphite-light border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Mecânica Industrial
                </Link>
                <Link
                  to="/caldeiraria-solda"
                  className="inline-flex items-center gap-2 bg-gp-graphite-light border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Caldeiraria e Solda
                </Link>
                <Link
                  to="/hidraulica-industrial"
                  className="inline-flex items-center gap-2 bg-gp-graphite-light border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Hidráulica Industrial
                </Link>
                <Link
                  to="/oficina"
                  className="inline-flex items-center gap-2 bg-gp-graphite-light border border-gp-graphite-lighter hover:border-gp-yellow/30 px-5 py-3 rounded-lg text-gray-300 hover:text-gp-yellow text-sm font-medium transition-all duration-300"
                >
                  <i className="ri-arrow-right-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  Oficina Especializada
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="relative rounded-xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-gp-yellow/20 to-gp-graphite"></div>
              <div className="absolute inset-0 bg-gp-graphite/60"></div>
              <div className="relative z-10 p-8 md:p-12 text-center">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                  Precisa de Major Repair em redutores industriais?
                </h2>
                <p className="text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                  Conte com a estrutura completa da oficina especializada Pilar para Major Repairs em equipamentos críticos do setor siderúrgico.
                  Mecânica, pneumática, caldeiraria e soldagem integradas em um único fornecedor homologado.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href="https://wa.me/5527993092922"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
                  >
                    <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center">
                      <i className="ri-whatsapp-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <span>Solicitar Orçamento</span>
                    <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                  </a>
                  <Link
                    to="/contato"
                    className="inline-flex items-center justify-center gap-2 border border-gray-500 hover:border-gp-yellow text-gray-300 hover:text-gp-yellow px-8 py-4 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap"
                  >
                    <i className="ri-mail-line text-lg w-6 h-6 flex items-center justify-center"></i>
                    Falar com Especialista
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Back to Cases */}
        <section className="py-10 border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link
                to="/cases"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para Cases
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-home-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />

      {/* Schema JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'Major Repair de Redutor de Içamento da Tampa de Panela de Gusa | Case Grupo Pilar',
            description: 'Case de recuperação completa de redutor industrial utilizado no sistema de içamento da tampa de panela de gusa, envolvendo mecânica, pneumática, caldeiraria e soldagem industrial.',
            image: heroImage,
            author: {
              '@type': 'Organization',
              name: 'Grupo Pilar',
              url: 'https://www.pilarmanutencao.com.br',
            },
            publisher: {
              '@type': 'Organization',
              name: 'Grupo Pilar',
              logo: {
                '@type': 'ImageObject',
                url: '/images/MARCA/LOGO-GRUPO-PILAR/LOGO-GRUPO-PILAR.png',
              },
            },
            datePublished: '2025-06-15',
            dateModified: '2026-06-23',
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': 'https://www.pilarmanutencao.com.br/cases/major-repair-redutor-tampa-panela-gusa',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: 'https://www.pilarmanutencao.com.br',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Cases de Sucesso',
                item: 'https://www.pilarmanutencao.com.br/cases',
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: 'Major Repair de Redutor de Içamento da Tampa de Panela de Gusa',
                item: 'https://www.pilarmanutencao.com.br/cases/major-repair-redutor-tampa-panela-gusa',
              },
            ],
          }),
        }}
      />
    </div>
  );
}